import React, { useState, ChangeEvent, useEffect, useCallback } from 'react';
import { X, Sparkles, Megaphone, Rocket, Tag, Plus, Bell, Loader, Lock, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { getAllNotifications, createNotification, activateNotification, deactivateNotification, deleteNotification } from '../api/Notifications.api';
import '../style/NotificationBanner.css'; 

export interface Anuncio {
    id?: string;
    isActive: boolean;
    type: 'promo' | 'anuncio' | 'cohete';
    title: string;
    description: string;
    buttonIsActive: boolean;
    buttonText: string;
    buttonLink: string;
}

const AdManager: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [anuncios, setAnuncios] = useState<Anuncio[]>([]);
  const [loading, setLoading] = useState(true);
  const [accessCode, setAccessCode] = useState('');
  const SECRET_CODE = "lisual2026"; 

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Anuncio>({
    isActive: false,
    type: 'promo',
    title: '',
    description: '',
    buttonIsActive: false,
    buttonText: '',
    buttonLink: ''
  });

  const IconMap = { promo: Tag, cohete: Rocket, anuncio: Megaphone };

  const fetchAnuncios = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllNotifications();
      setAnuncios(data || []);
    } catch (error) {
      toast.error("Error al cargar los anuncios.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Solo cargar anuncios si está autenticado
  useEffect(() => {
    if (isAuthenticated) {
        fetchAnuncios();
    }
  }, [fetchAnuncios, isAuthenticated]);

  
  const handleAccessSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (accessCode === SECRET_CODE) {
        setIsAuthenticated(true);
        toast.success("Acceso concedido");
    } else {
        toast.error("Código incorrecto");
        setAccessCode('');
    }
  };


  const toggleStatus = async (ad: Anuncio) => {
    if (!ad.id) return;

    try {
      if (ad.isActive) {
        await deactivateNotification(ad.id);
        toast.success(`Anuncio "${ad.title}" desactivado.`);
      } else {
        await activateNotification(ad.id);
        toast.success(`Anuncio "${ad.title}" activado.`);
      }
      await fetchAnuncios();
    } catch (error) {
      toast.error("Error al cambiar el estado del anuncio.");
      console.error(error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("¿Estás seguro de que quieres eliminar este anuncio?")) return;

    try {
      await deleteNotification(id);
      toast.success("Anuncio eliminado correctamente.");
      await fetchAnuncios();
    } catch (error) {
      toast.error("Error al eliminar el anuncio.");
      console.error(error);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const val = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData(prev => ({ ...prev, [name]: val }));
  };

  const handleSave = async () => {
    if (!formData.title || !formData.description) {
        toast.warn("El título y la descripción son obligatorios.");
        return;
    }
    try {
        await createNotification(formData);
        toast.success("Anuncio creado con éxito.");
        setIsModalOpen(false);
        setFormData({ isActive: false, type: 'promo', title: '', description: '', buttonIsActive: false, buttonText: '', buttonLink: '' });
        await fetchAnuncios();
    } catch (error) {
        toast.error("Error al crear el anuncio.");
        console.error(error);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock size={32} />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Panel Protegido</h2>
          <p className="text-gray-500 mb-6 text-sm">Ingresa el código de acceso para gestionar los anuncios.</p>
          <form onSubmit={handleAccessSubmit} className="space-y-4">
            <input 
              type="password" 
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              placeholder="Código de acceso..."
              className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-center font-mono tracking-widest"
              autoFocus
            />
            <button 
              type="submit"
              className="w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition-colors"
            >
              Desbloquear
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto min-h-screen">
      {/* Header Principal estilo UserModal */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Gestión de Anuncios</h1>
          <p className="text-gray-500 text-sm italic">Banners globales (solo uno activo)</p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium flex items-center gap-2 hover:bg-blue-700 transition-colors shadow-sm"
        >
          <Plus size={20} /> Crear anuncio
        </button>
      </div>

      {/* Lista de Anuncios */}
      {loading ? (
        <div className="flex justify-center items-center py-20">
            <Loader className="animate-spin text-blue-500" size={40} />
        </div>
      ) : (
        <div className="space-y-6">
          {anuncios.map((ad) => {
          const Icon = (IconMap as any)[ad.type] || Bell;
          const themeClass = `nb-theme-${ad.type}`;

          return (
            <div key={ad.id} className="relative group">
              {/* Control de Estado Estilo UserModal */}
              <div className="absolute -top-3 right-4 z-20 flex items-center gap-2">
                  <button 
                    onClick={() => handleDelete(ad.id!)}
                    className="flex items-center justify-center bg-white border rounded-full w-8 h-8 text-red-500 hover:bg-red-50 shadow-sm transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 size={14} />
                  </button>
                  <div className="flex items-center gap-3 bg-white border rounded-full px-4 py-1.5 shadow-sm">
                    <span className={`text-[11px] font-bold uppercase ${ad.isActive ? 'text-green-600' : 'text-gray-400'}`}>
                        {ad.isActive ? '• Activo' : '• Inactivo'}
                    </span>
                    <button 
                      onClick={() => toggleStatus(ad)}
                      className={`w-9 h-5 flex items-center rounded-full p-1 transition-colors ${ad.isActive ? 'bg-blue-600' : 'bg-gray-300'}`}
                    >
                      <div className={`bg-white w-3 h-3 rounded-full shadow-md transform transition-transform ${ad.isActive ? 'translate-x-4' : 'translate-x-0'}`} />
                    </button>
                  </div>
              </div>

              {/* Banner con tus estilos CSS */}
              <div className={`nb-main-wrapper ${themeClass} !mb-0 shadow-md`}>
                <div className="nb-inner-box">
                  <div className="nb-light-sweep" />
                  <div className="flex flex-col md:flex-row items-center justify-between gap-4 relative z-10">
                    <div className="flex items-center gap-4">
                      <div className="nb-icon-box">
                        <Icon className="text-white" size={24} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-base md:text-lg font-black text-gray-900 tracking-tight uppercase">
                            {ad.title}
                          </h3>
                          <Sparkles size={16} className="nb-sparkle-color animate-bounce" />
                        </div>
                        <p className="text-sm text-gray-600 font-medium">{ad.description}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        </div>
      )}

      {/* Modal de Configuración (Igual a UserModal) */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-0 md:p-4 z-50 overflow-y-auto">
          <div className="relative bg-white rounded-none md:rounded-xl w-full h-full md:h-auto max-w-4xl md:mt-16 md:mb-8 shadow-lg flex flex-col">
            
            {/* Header del Modal */}
            <div className="flex justify-between items-center border-b p-4 md:p-6">
              <h2 className="text-xl md:text-2xl font-bold">✨ Crear nuevo anuncio</h2>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-2xl md:text-xl">✕</button>
            </div>

            {/* Body del Modal */}
            <div className="p-4 md:p-6 space-y-6 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <h3 className="col-span-full text-lg font-semibold text-blue-600 border-b pb-2">Configuración Visual</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Título del Anuncio*</label>
                  <input 
                    type="text" 
                    name="title" 
                    value={formData.title} 
                    onChange={handleChange} 
                    placeholder="Ej: ¡NUEVO LANZAMIENTO!"
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none" 
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Estilo / Categoría*</label>
                  <select 
                    name="type" 
                    value={formData.type} 
                    onChange={handleChange} 
                    className="w-full border rounded-lg px-3 py-2 bg-white outline-none"
                  >
                    <option value="promo">Etiqueta Verde (Promo)</option>
                    <option value="anuncio">Megáfono Naranja (Anuncio)</option>
                    <option value="cohete">Cohete Rojo (Urgente)</option>
                  </select>
                </div>

                <div className="col-span-full">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Descripción*</label>
                  <textarea 
                    name="description" 
                    value={formData.description} 
                    onChange={handleChange} 
                    className="w-full border rounded-lg px-3 py-2 h-24 resize-none outline-none" 
                    placeholder="Escribe el mensaje que verán los usuarios..."
                  />
                </div>
              </div>

              {/* Sección de Botón con fondo de color igual a UserModal */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                <div className="border p-4 rounded-lg bg-blue-50 col-span-full">
                  <h4 className="font-semibold mb-4 flex items-center gap-2 text-blue-800">
                    <input 
                      type="checkbox" 
                      name="buttonIsActive" 
                      checked={formData.buttonIsActive} 
                      onChange={handleChange} 
                      className="h-4 w-4" 
                    />
                    Habilitar botón de acción (CTA)
                  </h4>
                  
                  <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 transition-opacity ${formData.buttonIsActive ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Texto del botón</label>
                      <input 
                        type="text" 
                        name="buttonText" 
                        value={formData.buttonText} 
                        onChange={handleChange} 
                        className="w-full border rounded-lg px-3 py-2 bg-white" 
                        placeholder="Ej: Ver más" 
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Link de redirección</label>
                      <input 
                        type="text" 
                        name="buttonLink" 
                        value={formData.buttonLink} 
                        onChange={handleChange} 
                        className="w-full border rounded-lg px-3 py-2 bg-white" 
                        placeholder="https://..." 
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer del Modal estilo UserModal */}
            <div className="flex flex-col sm:flex-row justify-end gap-3 border-t p-4 bg-gray-50 rounded-b-xl">
              <button 
                onClick={() => setIsModalOpen(false)} 
                className="px-6 py-2 bg-gray-300 rounded-lg font-medium hover:bg-gray-400 transition-colors"
              >
                Cancelar
              </button>
              <button 
                onClick={handleSave} 
                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Guardar anuncio
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    
  );
};

export default AdManager;